import dayjs from 'dayjs';
import {Calendar as CalendarIcon, ChevronLeft} from 'lucide-react';
import {useState} from 'react';
import type {DateRange} from 'react-day-picker';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import {Calendar} from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {TIME_FORMATS} from '@/utils/timeFormats';

export type DateRangeValue = {
  startDate: string;
  endDate: string;
};

export type DateRangeSelectProps = {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
};

type PresetKey =
  | 'today'
  | 'yesterday'
  | '7d'
  | '14d'
  | '30d'
  | 'thisWeek'
  | 'thisMonth';

const quickPresetKeys: PresetKey[] = ['today', 'yesterday'];
const trailingPresetKeys: PresetKey[] = ['7d', '14d', '30d'];
const calendarPresetKeys: PresetKey[] = ['thisWeek', 'thisMonth'];
const presetKeys: PresetKey[] = [
  ...quickPresetKeys,
  ...trailingPresetKeys,
  ...calendarPresetKeys,
];

const resolvePreset = (preset: PresetKey): DateRangeValue => {
  const today = dayjs().format(TIME_FORMATS.awsDate);

  switch (preset) {
    case 'today':
      return {startDate: today, endDate: today};
    case 'yesterday': {
      const yesterday = dayjs().subtract(1, 'day').format(TIME_FORMATS.awsDate);
      return {startDate: yesterday, endDate: yesterday};
    }
    case '7d':
      return {
        startDate: dayjs().subtract(6, 'day').format(TIME_FORMATS.awsDate),
        endDate: today,
      };
    case '14d':
      return {
        startDate: dayjs().subtract(13, 'day').format(TIME_FORMATS.awsDate),
        endDate: today,
      };
    case '30d':
      return {
        startDate: dayjs().subtract(29, 'day').format(TIME_FORMATS.awsDate),
        endDate: today,
      };
    case 'thisWeek':
      return {
        startDate: dayjs().startOf('isoWeek').format(TIME_FORMATS.awsDate),
        endDate: dayjs().endOf('isoWeek').format(TIME_FORMATS.awsDate),
      };
    case 'thisMonth':
      return {
        startDate: dayjs().startOf('month').format(TIME_FORMATS.awsDate),
        endDate: dayjs().endOf('month').format(TIME_FORMATS.awsDate),
      };
  }
};

const formatRangeLabel = (value: DateRangeValue) => {
  if (value.startDate === value.endDate) {
    return dayjs(value.startDate).format(TIME_FORMATS.date);
  }

  return `${dayjs(value.startDate).format(TIME_FORMATS.date)} - ${dayjs(
    value.endDate
  ).format(TIME_FORMATS.date)}`;
};

const isRangeLongerThanOneYear = (range: DateRange) => {
  if (!range.from || !range.to) return false;

  const from = dayjs(range.from).startOf('day');
  const to = dayjs(range.to).startOf('day');
  const [start, end] = from.isBefore(to) ? [from, to] : [to, from];

  return end.isAfter(start.add(1, 'year'), 'day');
};

type View = 'menu' | 'calendar';

const DateRangeSelect = ({value, onChange}: DateRangeSelectProps) => {
  const {t} = useTranslation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('menu');
  // Kept local (not derived every render from `value`) so the first click of
  // a custom range only updates this in-progress pick — committing it to
  // `onChange` on every click would feed it straight back into the Calendar's
  // controlled `selected` prop as a "complete" one-day range, which makes
  // react-day-picker think the range is already finished and start a new one
  // on the next click instead of extending it.
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>();

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setView('menu');
      return;
    }

    // If a custom (non-preset) range is already active, jump straight to the
    // calendar pre-filled with it — otherwise you'd land on the preset list
    // with no indication that a custom range is what's actually showing.
    if (matchedPreset) {
      setView('menu');
    } else {
      setPendingRange({
        from: dayjs(value.startDate).toDate(),
        to: dayjs(value.endDate).toDate(),
      });
      setView('calendar');
    }
  };

  const presetLabels: Record<PresetKey, string> = {
    today: t('Today'),
    yesterday: t('Yesterday'),
    '7d': t('Last 7 days'),
    '14d': t('Last 14 days'),
    '30d': t('Last 30 days'),
    thisWeek: t('This week'),
    thisMonth: t('This month'),
  };

  const matchedPreset = presetKeys.find(preset => {
    const resolved = resolvePreset(preset);
    return (
      resolved.startDate === value.startDate &&
      resolved.endDate === value.endDate
    );
  });

  const label = matchedPreset
    ? presetLabels[matchedPreset]
    : formatRangeLabel(value);

  const handlePresetClick = (preset: PresetKey) => {
    onChange(resolvePreset(preset));
    setOpen(false);
  };

  const renderPresetItem = (preset: PresetKey) => (
    <DropdownMenuCheckboxItem
      key={preset}
      checked={preset === matchedPreset}
      onSelect={() => handlePresetClick(preset)}
    >
      {presetLabels[preset]}
    </DropdownMenuCheckboxItem>
  );

  const openCustomRange = () => {
    // Start with a blank calendar rather than pre-filled with the current
    // value, so re-opening "Custom range" always reads as starting a fresh
    // pick rather than continuing to edit the previous one.
    setPendingRange(undefined);
    setView('calendar');
  };

  const handleCalendarSelect = (range: DateRange | undefined) => {
    if (range && isRangeLongerThanOneYear(range)) return;

    setPendingRange(range);

    if (!range?.from || !range.to) return;

    onChange({
      startDate: dayjs(range.from).format(TIME_FORMATS.awsDate),
      endDate: dayjs(range.to).format(TIME_FORMATS.awsDate),
    });
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="small"
          className="shrink-0"
        >
          <CalendarIcon className="size-4" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={view === 'menu' ? 'w-56' : 'w-auto'}
      >
        {view === 'menu' && (
          <>
            {quickPresetKeys.map(renderPresetItem)}
            <DropdownMenuSeparator />
            {trailingPresetKeys.map(renderPresetItem)}
            <DropdownMenuSeparator />
            {calendarPresetKeys.map(renderPresetItem)}
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={!matchedPreset && value.startDate !== value.endDate}
              onSelect={event => {
                event.preventDefault();
                openCustomRange();
              }}
            >
              {t('Custom range')}
            </DropdownMenuCheckboxItem>
          </>
        )}
        {view === 'calendar' && (
          <div className="flex flex-col gap-1">
            <Button
              type="button"
              variant="tertiary"
              size="small"
              className="self-start"
              onClick={() => setView('menu')}
            >
              <ChevronLeft className="size-4" />
              {t('Back')}
            </Button>
            <Calendar
              mode="range"
              resetOnSelect
              selected={pendingRange}
              onSelect={handleCalendarSelect}
              disabled={date => {
                if (!pendingRange?.from || pendingRange.to) return false;

                const selectedStart = dayjs(pendingRange.from).startOf('day');
                const dateToCheck = dayjs(date).startOf('day');

                return (
                  dateToCheck.isBefore(
                    selectedStart.subtract(1, 'year'),
                    'day'
                  ) || dateToCheck.isAfter(selectedStart.add(1, 'year'), 'day')
                );
              }}
              numberOfMonths={2}
              autoFocus
            />
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DateRangeSelect;
